import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../../../../../utils/api";
import ProductFormComponent from "./ProductFormComponent";

const ProductForm = ({ isEdit = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [removeImage, setRemoveImage] = useState(false);
  const [error, setError] = useState("");

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    image: null,
    imagePreview: null,
    currentAttachments: [],
    features: [],
    specifications: [],
    certifications: [],
    testingCapabilities: [],
    validationMethods: [],
  });

  const [tempValues, setTempValues] = useState({
    feature: "",
    specName: "",
    specValue: "",
    certification: "",
    testingCapability: "",
    validationMethod: "",
  });

  const [validationErrors, setValidationErrors] = useState({});
  const [editingItem, setEditingItem] = useState({
    type: null,
    index: -1,
  });

  // Fetch product data for edit mode
  useEffect(() => {
    if (!isEdit || !id) return;

    const fetchProduct = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/api/products/${id}`);
        const product = response.data;
        setFormData({
          id: product.id,
          title: product.title || "",
          description: product.description || "",
          image: null,
          imagePreview: product.image_url || null,
          currentAttachments: product.image_url
            ? [
                {
                  id: product.id,
                  file_name: product.image_url.split("/").pop(),
                  file_path: product.image_url,
                  file_type: "image/jpeg",
                  size: 0,
                },
              ]
            : [],
          features: product.features?.map((f) => f.feature_name) || [],
          specifications:
            product.specifications?.map((s) => ({
              name: s.spec_name,
              value: s.spec_value,
            })) || [],
          certifications:
            product.certifications?.map((c) => c.certification_name) || [],
          testingCapabilities:
            product.testingCapabilities?.map((t) => t.capability_name) || [],
          validationMethods:
            product.validationMethods?.map((v) => v.method_name) || [],
        });
      } catch (error) {
        console.error("Error fetching product:", error);
        setError("Failed to load product data");
        toast.error("Failed to load product data");
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id, isEdit]);

  // Form validation
  const validateForm = () => {
    const errors = {};

    if (!formData.title.trim()) {
      errors.title = "Product title is required";
    }

    if (formData.title.length > 100) {
      errors.title = "Title must be less than 100 characters";
    }

    if (formData.description && formData.description.length > 500) {
      errors.description = "Description must be less than 500 characters";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      window.scrollTo(0, 0);
      return;
    }

    try {
      setSubmitting(true);
      setError("");

      const formDataToSend = new FormData();
      formDataToSend.append("title", formData.title);
      formDataToSend.append("description", formData.description || "");

      if (formData.image) {
        formDataToSend.append("image", formData.image);
      }

      if (isEdit && removeImage) {
        formDataToSend.append("removeImage", "true");
      }

      formDataToSend.append("features", JSON.stringify(formData.features));
      formDataToSend.append(
        "specifications",
        JSON.stringify(formData.specifications)
      );
      formDataToSend.append(
        "certifications",
        JSON.stringify(formData.certifications)
      );
      formDataToSend.append(
        "testingCapabilities",
        JSON.stringify(formData.testingCapabilities)
      );
      formDataToSend.append(
        "validationMethods",
        JSON.stringify(formData.validationMethods)
      );

      if (isEdit) {
        await api.put(`/api/products/${id}`, formDataToSend, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Product updated successfully");
      } else {
        await api.post("/api/products", formDataToSend, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Product created successfully");
      }

      navigate("/admin/products");
    } catch (error) {
      console.error(
        `Error ${isEdit ? "updating" : "creating"} product:`,
        error
      );
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        `Failed to ${isEdit ? "update" : "create"} product`;
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    navigate("/admin/products");
  };

  // Handle basic input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle image changes
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData((prev) => ({
        ...prev,
        image: file,
        imagePreview: URL.createObjectURL(file),
        currentAttachments: [], // Clear existing attachments when new image is selected
      }));
      setRemoveImage(false);
    }
  };

  // Handle image removal
  const handleImageRemove = () => {
    setFormData((prev) => ({
      ...prev,
      image: null,
      imagePreview: null,
      currentAttachments: [],
    }));
    setRemoveImage(true);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Add item to dynamic lists
  const addItem = (arrayName) => {
    if (arrayName === "features") {
      if (!tempValues.feature.trim()) return;

      if (editingItem.type === arrayName && editingItem.index >= 0) {
        // Update existing item
        setFormData((prev) => ({
          ...prev,
          features: prev.features.map((item, index) =>
            index === editingItem.index ? tempValues.feature.trim() : item
          ),
        }));
      } else {
        // Add new item
        setFormData((prev) => ({
          ...prev,
          features: [...prev.features, tempValues.feature.trim()],
        }));
      }
      setTempValues((prev) => ({ ...prev, feature: "" }));
    } else if (arrayName === "specifications") {
      if (!tempValues.specName.trim() || !tempValues.specValue.trim()) return;

      const newSpec = {
        name: tempValues.specName.trim(),
        value: tempValues.specValue.trim(),
      };

      if (editingItem.type === arrayName && editingItem.index >= 0) {
        // Update existing item
        setFormData((prev) => ({
          ...prev,
          specifications: prev.specifications.map((item, index) =>
            index === editingItem.index ? newSpec : item
          ),
        }));
      } else {
        // Add new item
        setFormData((prev) => ({
          ...prev,
          specifications: [...prev.specifications, newSpec],
        }));
      }
      setTempValues((prev) => ({ ...prev, specName: "", specValue: "" }));
    } else if (arrayName === "certifications") {
      if (!tempValues.certification.trim()) return;

      if (editingItem.type === arrayName && editingItem.index >= 0) {
        // Update existing item
        setFormData((prev) => ({
          ...prev,
          certifications: prev.certifications.map((item, index) =>
            index === editingItem.index ? tempValues.certification.trim() : item
          ),
        }));
      } else {
        // Add new item
        setFormData((prev) => ({
          ...prev,
          certifications: [
            ...prev.certifications,
            tempValues.certification.trim(),
          ],
        }));
      }
      setTempValues((prev) => ({ ...prev, certification: "" }));
    } else if (arrayName === "testingCapabilities") {
      if (!tempValues.testingCapability.trim()) return;

      if (editingItem.type === arrayName && editingItem.index >= 0) {
        // Update existing item
        setFormData((prev) => ({
          ...prev,
          testingCapabilities: prev.testingCapabilities.map((item, index) =>
            index === editingItem.index
              ? tempValues.testingCapability.trim()
              : item
          ),
        }));
      } else {
        // Add new item
        setFormData((prev) => ({
          ...prev,
          testingCapabilities: [
            ...prev.testingCapabilities,
            tempValues.testingCapability.trim(),
          ],
        }));
      }
      setTempValues((prev) => ({ ...prev, testingCapability: "" }));
    } else if (arrayName === "validationMethods") {
      if (!tempValues.validationMethod.trim()) return;

      if (editingItem.type === arrayName && editingItem.index >= 0) {
        // Update existing item
        setFormData((prev) => ({
          ...prev,
          validationMethods: prev.validationMethods.map((item, index) =>
            index === editingItem.index
              ? tempValues.validationMethod.trim()
              : item
          ),
        }));
      } else {
        // Add new item
        setFormData((prev) => ({
          ...prev,
          validationMethods: [
            ...prev.validationMethods,
            tempValues.validationMethod.trim(),
          ],
        }));
      }
      setTempValues((prev) => ({ ...prev, validationMethod: "" }));
    }

    // Reset editing state
    setEditingItem({ type: null, index: -1 });
  };

  // Start editing an item
  const startEditing = (arrayName, index) => {
    const item = formData[arrayName][index];

    setEditingItem({ type: arrayName, index });

    if (arrayName === "features") {
      setTempValues((prev) => ({ ...prev, feature: item }));
    } else if (arrayName === "specifications") {
      setTempValues((prev) => ({
        ...prev,
        specName: item.name,
        specValue: item.value,
      }));
    } else if (arrayName === "certifications") {
      setTempValues((prev) => ({ ...prev, certification: item }));
    } else if (arrayName === "testingCapabilities") {
      setTempValues((prev) => ({ ...prev, testingCapability: item }));
    } else if (arrayName === "validationMethods") {
      setTempValues((prev) => ({ ...prev, validationMethod: item }));
    }
  };

  // Update item (same as addItem when editing)
  const updateItem = () => {
    addItem(editingItem.type);
  };

  // Cancel editing
  const cancelEditing = () => {
    setEditingItem({ type: null, index: -1 });
    setTempValues({
      feature: "",
      specName: "",
      specValue: "",
      certification: "",
      testingCapability: "",
      validationMethod: "",
    });
  };

  // Remove item from list
  const removeItem = (arrayName, index) => {
    setFormData((prev) => ({
      ...prev,
      [arrayName]: prev[arrayName].filter((_, i) => i !== index),
    }));

    // Cancel editing if we're editing the item being removed
    if (editingItem.type === arrayName && editingItem.index === index) {
      cancelEditing();
    }
  };

  // Handle key press (already handled in component)
  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
    }
  };

  if (loading) {
    return <div className="p-4 text-center">Loading...</div>;
  }

  return (
    <ProductFormComponent
      formData={formData}
      setFormData={setFormData}
      tempValues={tempValues}
      setTempValues={setTempValues}
      validationErrors={validationErrors}
      editingItem={editingItem}
      fileInputRef={fileInputRef}
      handleChange={handleChange}
      handleImageChange={handleImageChange}
      handleImageRemove={handleImageRemove}
      handleSubmit={handleSubmit}
      onCancel={handleCancel}
      addItem={addItem}
      startEditing={startEditing}
      updateItem={updateItem}
      cancelEditing={cancelEditing}
      removeItem={removeItem}
      handleKeyPress={handleKeyPress}
      loading={loading}
      submitting={submitting}
      error={error}
      isEdit={isEdit}
    />
  );
};

export default ProductForm;
